import { useState } from "react";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { ProfileContactInfo } from "@ludens-reklame/rubics-v3-sdk/profile/types";
import { useCart } from "@ludens-reklame/rubics-v3-react/cart/hooks";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { useProfile } from "@ludens-reklame/rubics-v3-react/profile/hooks";
import { createOrder } from "@ludens-reklame/rubics-v3-sdk/order";
import { Order as IOrder } from "@ludens-reklame/rubics-v3-sdk/order/types";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import {
  Constrainer,
  HorizontalSpacer,
} from "../../internal-components/Layout.js";
import {
  ButtonList,
  Card,
  DiscountCode,
  DiscountCodeButtonWrapper,
  Expander,
  Layout,
  OrderSummary,
  OrderSummaryItem,
  OrderWrapper,
  Total,
  Wrapper,
} from "./styles.js";
import { formatCurrency } from "../../util/formatter.js";
import { Input } from "../../internal-components/Input/Input.js";
import { Button, ButtonAction } from "../../internal-components/Button.js";
import { Busy } from "../../internal-components/Busy.js";
import { Ruler } from "../../internal-components/product/Ruler.js";
import { InputGrid } from "../MyPage/styles.js";
import { chevron } from "../../icons/index.js";
import { OrderTable } from "../MyPage/pages/Orders.js";
import { useLinkProps } from "../../hooks/useLinkProps.js";

interface CheckoutProps {
  termsAndConditions?: Link;
}

export const Checkout: React.FC<CheckoutProps> = ({ termsAndConditions }) => {
  const store = useStore();
  const { profile, networking: loading } = useProfile();
  const { cart, addDiscountCode, removeDiscountCode, networking } = useCart();
  const [discountCode, setDiscountCode] = useState<string>("");
  const [contactInfo, setContactInfo] = useState<ProfileContactInfo>({});
  const [creatingOrder, setCreatingOrder] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [order, setOrder] = useState<IOrder | undefined>(undefined);
  const termsAndConditionProps = useLinkProps(termsAndConditions);

  if (order) {
    return (
      <Wrapper>
        <HorizontalSpacer>
          <Constrainer>
            <Button as="a" href="/" $variant="tertiary" className="back">
              Tilbake til startsiden
            </Button>
            <Card>
              <h1 className="h4">Takk for handelen</h1>
              {store.context.loggedIn && (
                <p>
                  Du kan se mer om din ordre på{" "}
                  <a href="/min-side">Min side.</a>
                </p>
              )}
              <Ruler />
              <p>Oversikt over din bestilling</p>
              <OrderWrapper>
                <OrderTable order={order} />
              </OrderWrapper>
            </Card>
          </Constrainer>
        </HorizontalSpacer>
      </Wrapper>
    );
  }

  const discountCodeNode = (
    <DiscountCode
      onSubmit={(e) => {
        e.preventDefault();
        addDiscountCode(discountCode);
        setDiscountCode("");
      }}
    >
      <Input
        label="Har du en rabattkode?"
        value={discountCode}
        onChange={(e) => setDiscountCode(e.target.value)}
        placeholder="Skriv inn rabattkoden din her"
      />
      <DiscountCodeButtonWrapper>
        <Button type="submit" $variant="secondary" disabled={networking}>
          {networking ? "Laster…" : "Aktiver rabattkode"}
        </Button>
      </DiscountCodeButtonWrapper>
    </DiscountCode>
  );

  return (
    <Wrapper>
      <HorizontalSpacer>
        <Constrainer>
          <Busy $busy={creatingOrder}>
            <Layout>
              <div>
                <Card>
                  <h2 className="h4">
                    <Expander
                      $expanded={expanded}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setExpanded(!expanded);
                      }}
                      aria-label={`${
                        expanded ? "Minimer" : "Ekspander"
                      } bestillingsoversikt`}
                    >
                      Bestillingsoversikt {chevron}
                    </Expander>
                  </h2>
                  <Busy $busy={networking}>
                    <OrderSummary $expanded={expanded}>
                      {cart.items.map((i) => (
                        <OrderSummaryItem key={i._id}>
                          {i.product.primaryImage && (
                            <img
                              src={transformImageUrl(i.product.primaryImage, {
                                width: 108,
                                height: 108,
                                fit: "contain",
                              })}
                              alt={i.product.primaryImage.altText}
                            />
                          )}
                          <div className="line-info">
                            <p>
                              {i.product.name} x {i.quantityText}
                            </p>
                            {i.attributes.map((a) => (
                              <p key={a._id}>{a.value}</p>
                            ))}
                          </div>
                          <div className="line-cost">
                            <p>{i.totalText}</p>
                            {i.subtotal !== i.total && (
                              <p className="line-cost-compare">
                                {formatCurrency(i.subtotal + i.subtotalTax)}
                              </p>
                            )}
                          </div>
                        </OrderSummaryItem>
                      ))}
                      <OrderSummaryItem>
                        <div className="line-info">
                          {cart.discountLines.map((d) => (
                            <p key={d._id}>
                              {d.code || d.name}:
                              {d.code && (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    removeDiscountCode(d.code!);
                                  }}
                                >
                                  {" ("}Fjern{")"}
                                </a>
                              )}
                            </p>
                          ))}
                          <p>MVA:</p>
                        </div>
                        <div className="line-cost">
                          {cart.discountLines.map((d) => (
                            <p key={d._id}>- {d.totalText}</p>
                          ))}
                          <p>{cart.totalTaxText}</p>
                        </div>
                      </OrderSummaryItem>
                    </OrderSummary>
                    <Total>
                      <span>Totalsum:</span>
                      <span>{cart.totalText}</span>
                    </Total>
                    <div className="desktop">{discountCodeNode}</div>
                  </Busy>
                </Card>
                <Card className="mobile">{discountCodeNode}</Card>
              </div>
              <div>
                {!store.context.loggedIn && (
                  <Card>
                    <p>
                      <a href="/logg-inn">
                        Logg inn eller opprett ny brukerkonto.
                      </a>{" "}
                      Som innlogget kunde får du tilgang til din ordrehistorikk.
                      Du kan også fortsette å handle som gjest.
                    </p>
                  </Card>
                )}
                <Card>
                  <h2 className="h4">Kontaktinformasjon</h2>
                  <Ruler />
                  <Busy $busy={loading}>
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();

                        if (loading || networking) {
                          return;
                        }

                        setCreatingOrder(true);

                        const order = await createOrder({
                          billing: contactInfo,
                        });

                        if (!order) {
                          window.alert(
                            "Noe galt skjedde, vennligst prøv igjen"
                          );
                        }

                        setOrder(order);
                        setCreatingOrder(false);
                      }}
                    >
                      <InputGrid>
                        <Input
                          label="Fornavn"
                          autoComplete="given-name"
                          required
                          placeholder="Ola"
                          value={
                            contactInfo.firstName || profile?.firstName || ""
                          }
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              firstName: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Etternavn"
                          autoComplete="family-name"
                          required
                          placeholder="Nordmann"
                          value={
                            contactInfo.lastName || profile?.lastName || ""
                          }
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              lastName: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="E-postadresse"
                          type="email"
                          autoComplete="email"
                          required
                          placeholder="eksempel@epost.no"
                          value={contactInfo.email || profile?.email || ""}
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              email: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Adresse"
                          placeholder="Adresseveien 1"
                          autoComplete="street-address"
                          value={
                            contactInfo.addressLine1 ||
                            profile?.addressLine1 ||
                            ""
                          }
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              addressLine1: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Postnummer"
                          placeholder="0123"
                          autoComplete="postal-code"
                          value={
                            contactInfo.postcode || profile?.postcode || ""
                          }
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              postcode: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Sted"
                          placeholder="Oslo"
                          autoComplete="address-level2"
                          value={contactInfo.city || profile?.city || ""}
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              city: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Telefonnummer"
                          placeholder="123 45 678"
                          autoComplete="tel-local"
                          value={contactInfo.phone || profile?.phone || ""}
                          onChange={(e) =>
                            setContactInfo({
                              ...contactInfo,
                              phone: e.target.value,
                            })
                          }
                        />
                      </InputGrid>
                      {termsAndConditionProps &&
                        termsAndConditionProps.href && (
                          <p>
                            Ved å sende inn din bestilling godtar du våre{" "}
                            <a {...termsAndConditionProps}>
                              {" "}
                              {termsAndConditions?.title
                                ? termsAndConditions.title
                                : "salgsbetingelser"}
                            </a>
                            .
                          </p>
                        )}
                      <ButtonList>
                        <Button
                          type="submit"
                          $variant="primary"
                          disabled={loading || networking || creatingOrder}
                        >
                          {creatingOrder ? "Laster…" : "Send henvendelse"}
                        </Button>
                        <ButtonAction href="/handlekurv" variant="tertiary">
                          Tilbake til handlekurv
                        </ButtonAction>
                      </ButtonList>
                    </form>
                  </Busy>
                </Card>
              </div>
            </Layout>
          </Busy>
        </Constrainer>
      </HorizontalSpacer>
    </Wrapper>
  );
};
